import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const InvoiceModal = ({ invoice, isOpen, onClose, onSave, mode = 'view' }) => {
  const [formData, setFormData] = useState({
    number: invoice?.number || '',
    client: invoice?.client || '',
    clientEmail: invoice?.clientEmail || '',
    clientAddress: invoice?.clientAddress || '',
    date: invoice?.date || new Date().toISOString().split('T')[0],
    dueDate: invoice?.dueDate || '',
    items: invoice?.items || [
      { id: 1, description: '', quantity: 1, price: 0, tax: 21 }
    ],
    notes: invoice?.notes || '',
    paymentTerms: invoice?.paymentTerms || 'net30'
  });

  const [activeTab, setActiveTab] = useState('details');

  if (!isOpen) return null;

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: field === 'quantity' || field === 'price' || field === 'tax' ? parseFloat(value) || 0 : value
    };
    setFormData(prev => ({
      ...prev,
      items: updatedItems
    }));
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items,
        { id: Date.now(), description: '', quantity: 1, price: 0, tax: 21 }
      ]
    }));
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index)
      }));
    }
  };

  const calculateTotals = () => {
    const subtotal = formData.items.reduce((sum, item) => {
      return sum + (item.quantity * item.price);
    }, 0);

    const totalTax = formData.items.reduce((sum, item) => {
      const itemTotal = item.quantity * item.price;
      return sum + (itemTotal * (item.tax / 100));
    }, 0);

    const total = subtotal + totalTax;

    return { subtotal, totalTax, total };
  };

  const { subtotal, totalTax, total } = calculateTotals();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const handleSave = () => {
    const invoiceData = {
      ...formData,
      amount: total,
      subtotal,
      tax: totalTax,
      id: invoice?.id || Date.now()
    };
    onSave(invoiceData);
  };

  const tabs = [
    { id: 'details', label: 'Detalles', icon: 'FileText' },
    { id: 'items', label: 'Productos', icon: 'Package' },
    { id: 'payment', label: 'Pago', icon: 'CreditCard' },
    { id: 'history', label: 'Historial', icon: 'Clock' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-modal p-4">
      <div className="bg-surface rounded-lg shadow-modal max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <Icon name="FileText" size={24} className="text-primary" />
            <div>
              <h2 className="text-xl font-heading-semibold text-text-primary">
                {mode === 'create' ? 'Nueva Factura' : 
                 mode === 'edit' ? 'Editar Factura' : 
                 `Factura ${invoice?.number}`}
              </h2>
              {invoice && (
                <p className="text-sm text-text-secondary">
                  Cliente: {invoice.client}
                </p>
              )}
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {mode === 'view' && (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  iconName="Download"
                  iconSize={16}
                >
                  Descargar PDF
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  iconName="Send"
                  iconSize={16}
                >
                  Enviar por Email
                </Button>
              </>
            )}
            
            <button
              onClick={onClose}
              className="p-2 hover:bg-secondary-50 rounded-lg transition-micro"
            >
              <Icon name="X" size={20} className="text-text-secondary" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-border">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-body-medium text-sm transition-micro ${
                  activeTab === tab.id
                    ? 'border-primary text-primary' :'border-transparent text-text-secondary hover:text-text-primary'
                }`}
              >
                <Icon name={tab.icon} size={16} />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {activeTab === 'details' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-body-medium text-text-primary mb-2">
                    Número de Factura
                  </label>
                  <Input
                    type="text"
                    value={formData.number}
                    onChange={(e) => handleInputChange('number', e.target.value)}
                    placeholder="INV-2024-001"
                    disabled={mode === 'view'}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-body-medium text-text-primary mb-2">
                    Fecha de Factura
                  </label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => handleInputChange('date', e.target.value)}
                    disabled={mode === 'view'}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-body-medium text-text-primary mb-2">
                    Cliente
                  </label>
                  <Input
                    type="text"
                    value={formData.client}
                    onChange={(e) => handleInputChange('client', e.target.value)}
                    placeholder="Nombre del cliente"
                    disabled={mode === 'view'}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-body-medium text-text-primary mb-2">
                    Email del Cliente
                  </label>
                  <Input
                    type="email"
                    value={formData.clientEmail}
                    onChange={(e) => handleInputChange('clientEmail', e.target.value)}
                    placeholder="cliente@email.com"
                    disabled={mode === 'view'}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-body-medium text-text-primary mb-2">
                  Dirección del Cliente
                </label>
                <textarea
                  value={formData.clientAddress}
                  onChange={(e) => handleInputChange('clientAddress', e.target.value)}
                  placeholder="Dirección completa del cliente"
                  disabled={mode === 'view'}
                  rows={3}
                  className="w-full px-3 py-2 border border-input rounded-md bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-secondary-50 disabled:text-text-muted"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-body-medium text-text-primary mb-2">
                    Fecha de Vencimiento
                  </label>
                  <Input
                    type="date"
                    value={formData.dueDate}
                    onChange={(e) => handleInputChange('dueDate', e.target.value)}
                    disabled={mode === 'view'}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-body-medium text-text-primary mb-2">
                    Términos de Pago
                  </label>
                  <select
                    value={formData.paymentTerms}
                    onChange={(e) => handleInputChange('paymentTerms', e.target.value)}
                    disabled={mode === 'view'}
                    className="w-full px-3 py-2 border border-input rounded-md bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-secondary-50 disabled:text-text-muted"
                  >
                    <option value="immediate">Pago Inmediato</option>
                    <option value="net15">15 días</option>
                    <option value="net30">30 días</option>
                    <option value="net60">60 días</option>
                    <option value="net90">90 días</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'items' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-heading-semibold text-text-primary">
                  Productos y Servicios
                </h3>
                {mode !== 'view' && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={addItem}
                    iconName="Plus"
                    iconSize={16}
                  >
                    Agregar Producto
                  </Button>
                )}
              </div>

              <div className="space-y-4">
                {formData.items.map((item, index) => (
                  <div key={item.id} className="border border-border rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                      <div className="md:col-span-5">
                        <label className="block text-sm font-body-medium text-text-primary mb-2">
                          Descripción
                        </label>
                        <Input
                          type="text"
                          value={item.description}
                          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                          placeholder="Descripción del producto o servicio"
                          disabled={mode === 'view'}
                        />
                      </div>
                      
                      <div className="md:col-span-2">
                        <label className="block text-sm font-body-medium text-text-primary mb-2">
                          Cantidad
                        </label>
                        <Input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                          min="1"
                          disabled={mode === 'view'}
                        />
                      </div>
                      
                      <div className="md:col-span-2">
                        <label className="block text-sm font-body-medium text-text-primary mb-2">
                          Precio (€)
                        </label>
                        <Input
                          type="number"
                          value={item.price}
                          onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                          min="0"
                          step="0.01"
                          disabled={mode === 'view'}
                        />
                      </div>
                      
                      <div className="md:col-span-2">
                        <label className="block text-sm font-body-medium text-text-primary mb-2">
                          IVA (%)
                        </label>
                        <select
                          value={item.tax}
                          onChange={(e) => handleItemChange(index, 'tax', e.target.value)}
                          disabled={mode === 'view'}
                          className="w-full px-3 py-2 border border-input rounded-md bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-secondary-50 disabled:text-text-muted"
                        >
                          <option value="0">0%</option>
                          <option value="4">4%</option>
                          <option value="10">10%</option>
                          <option value="21">21%</option>
                        </select>
                      </div>
                      
                      <div className="md:col-span-1">
                        {mode !== 'view' && formData.items.length > 1 && (
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => removeItem(index)}
                            iconName="Trash2"
                            iconSize={16}
                          />
                        )}
                      </div>
                    </div>
                    
                    <div className="mt-3 text-right">
                      <span className="text-sm text-text-secondary">
                        Subtotal: {formatCurrency(item.quantity * item.price)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div className="border-t border-border pt-4">
                <div className="max-w-md ml-auto space-y-2">
                  <div className="flex justify-between">
                    <span className="text-text-secondary">Subtotal:</span>
                    <span className="font-body-medium">{formatCurrency(subtotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-text-secondary">IVA:</span>
                    <span className="font-body-medium">{formatCurrency(totalTax)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-heading-semibold border-t border-border pt-2">
                    <span>Total:</span>
                    <span>{formatCurrency(total)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'payment' && (
            <div className="space-y-6">
              <h3 className="text-lg font-heading-semibold text-text-primary">
                Información de Pago
              </h3>
              
              {invoice && (
                <div className="bg-secondary-50 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <div className="text-sm text-text-secondary">Estado</div>
                      <div className="font-body-medium text-text-primary">
                        {invoice.status === 'paid' ? 'Pagada' : 
                         invoice.status === 'overdue' ? 'Vencida' : 'Pendiente'}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-text-secondary">Importe Total</div>
                      <div className="font-body-medium text-text-primary">
                        {formatCurrency(invoice.amount)}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-text-secondary">Importe Pagado</div>
                      <div className="font-body-medium text-text-primary">
                        {formatCurrency(invoice.paidAmount || 0)}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-body-medium text-text-primary mb-2">
                  Notas de Pago
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Instrucciones de pago, número de cuenta, etc."
                  disabled={mode === 'view'}
                  rows={4}
                  className="w-full px-3 py-2 border border-input rounded-md bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:bg-secondary-50 disabled:text-text-muted"
                />
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <div className="space-y-6">
              <h3 className="text-lg font-heading-semibold text-text-primary">
                Historial de la Factura
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3 p-3 bg-secondary-50 rounded-lg">
                  <div className="p-1 bg-success-50 rounded-full">
                    <Icon name="Plus" size={12} className="text-success" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-body-medium text-text-primary">
                      Factura creada
                    </div>
                    <div className="text-xs text-text-secondary">
                      {invoice ? new Date(invoice.date).toLocaleDateString('es-ES') : 'Hoy'} - Sistema
                    </div>
                  </div>
                </div>

                {invoice?.status !== 'draft' && (
                  <div className="flex items-start space-x-3 p-3 bg-secondary-50 rounded-lg">
                    <div className="p-1 bg-primary-50 rounded-full">
                      <Icon name="Send" size={12} className="text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-body-medium text-text-primary">
                        Factura enviada al cliente
                      </div>
                      <div className="text-xs text-text-secondary">
                        {invoice ? new Date(invoice.date).toLocaleDateString('es-ES') : 'Hoy'} - {invoice?.clientEmail}
                      </div>
                    </div>
                  </div>
                )}

                {invoice?.status === 'paid' && (
                  <div className="flex items-start space-x-3 p-3 bg-secondary-50 rounded-lg">
                    <div className="p-1 bg-success-50 rounded-full">
                      <Icon name="CheckCircle" size={12} className="text-success" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-body-medium text-text-primary">
                        Pago recibido
                      </div>
                      <div className="text-xs text-text-secondary">
                        {invoice ? new Date(invoice.date).toLocaleDateString('es-ES') : 'Hoy'} - {formatCurrency(invoice?.amount || 0)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border">
          <Button
            variant="secondary"
            onClick={onClose}
          >
            {mode === 'view' ? 'Cerrar' : 'Cancelar'}
          </Button>
          
          {mode !== 'view' && (
            <Button
              variant="primary"
              onClick={handleSave}
              iconName="Save"
              iconSize={16}
            >
              {mode === 'create' ? 'Crear Factura' : 'Guardar Cambios'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoiceModal;